/**
 * ContractTemplatesTab — manage the tenant's contract templates (CB-10.4, FR-23).
 *
 * Mounted as the third tab of Settings → Templates, mirroring the Pipeline Templates card/table
 * interface: a table of templates with ghost-icon Duplicate / Edit / Delete actions, a scope badge,
 * a SlidePanel for editing, and an AlertDialog confirm for removal. The load-bearing rule is the
 * delete semantics — a template that has produced a contract is ARCHIVED (soft-delete, audit lineage
 * preserved); a never-used one is HARD-DELETED. That decision is the BACKEND's (it needs service_role
 * to read the admin-only contracts table); this tab only pre-flights the usage so the confirm copy is
 * right and reflects the outcome.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Pencil, Trash2, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SlidePanel } from '@/components/ui/slide-panel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { contractService } from '@/services/contractService';
import type { ContractTemplate } from '@/types/contracts';
import { SendRoleSelector } from './SendRoleSelector';

/** Pipeline-type scope options (mirror `pipelines.pipeline_type`). None selected = all types. */
const PIPELINE_TYPE_OPTIONS = ['residential', 'commercial', 'onboarding'] as const;

export function ContractTemplatesTab() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);

  const [editing, setEditing] = useState<ContractTemplate | null>(null);
  const [editName, setEditName] = useState('');
  const [editRoles, setEditRoles] = useState<number[]>([]);
  const [editPipelineTypes, setEditPipelineTypes] = useState<string[]>([]);

  const [removing, setRemoving] = useState<ContractTemplate | null>(null);

  const listQ = useQuery({
    queryKey: ['contractTemplates', 'manage', showArchived],
    queryFn: async () => {
      const r = await contractService.listTemplates(showArchived);
      if (!r.success) throw new Error(r.error);
      return r.data ?? [];
    },
  });
  const templates = useMemo(() => listQ.data ?? [], [listQ.data]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['contractTemplates'] });

  // Pre-flight the used-check so the confirm copy reads correctly.
  const usageQ = useQuery({
    queryKey: ['contractTemplateUsage', removing?.id],
    queryFn: async () => {
      const r = await contractService.getTemplateUsage(removing!.id);
      if (!r.success) throw new Error(r.error);
      return r.data!;
    },
    enabled: !!removing,
  });
  // P4 (review): only an EXPLICIT never-used result hard-deletes. A usage-check error/unknown must
  // mirror the backend fail-safe (treat as used → archive), so the copy never promises "permanently
  // deleted" while the server actually archives.
  const willHardDelete = usageQ.data?.used === false;

  const duplicateMut = useMutation({
    mutationFn: async (t: ContractTemplate) => {
      const r = await contractService.duplicateTemplate(t.id);
      if (!r.success) throw new Error(r.error);
      return r.data!;
    },
    onSuccess: () => {
      toast.success('Template duplicated');
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to duplicate'),
  });

  const editMut = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const r = await contractService.updateTemplate(editing.id, {
        name: editName.trim(),
        allowed_permission_roles: editRoles,
        pipeline_types: editPipelineTypes,
      });
      if (!r.success) throw new Error(r.error);
    },
    onSuccess: () => {
      toast.success('Template updated');
      setEditing(null);
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to update'),
  });

  const removeMut = useMutation({
    mutationFn: async () => {
      if (!removing) return { archived: false };
      const r = await contractService.deleteTemplate(removing.id);
      if (!r.success) throw new Error(r.error);
      return r.data!;
    },
    onSuccess: (res) => {
      toast.success(res.archived ? 'Template archived' : 'Template deleted');
      setRemoving(null);
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to remove'),
  });

  const openEdit = (t: ContractTemplate) => {
    setEditing(t);
    setEditName(t.name);
    setEditRoles(t.allowed_permission_roles ?? []);
    setEditPipelineTypes(t.pipeline_types ?? []);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Contract Templates</h3>
          <p className="text-sm text-muted-foreground">
            Reusable contracts your team can send. Create one by saving a draft as a template.
          </p>
        </div>
        <Label className="flex items-center gap-2 text-sm">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} />
          Show archived
        </Label>
      </div>

      {listQ.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading templates&hellip;
        </div>
      ) : templates.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No contract templates yet. Open a draft contract&rsquo;s review step and choose
          &ldquo;Save as template&rdquo;.
        </p>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Who can send</TableHead>
                <TableHead>Fields</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">
                    {t.name}
                    {t.archived && (
                      <Badge variant="outline" className="ml-2 text-muted-foreground">
                        Archived
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {t.document_source === 'built' ? 'Built' : 'Uploaded PDF'}
                    </Badge>
                    <Badge variant="outline" className="ml-1 capitalize">
                      {t.pipeline_types?.length ? t.pipeline_types.join(', ') : 'All pipelines'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={t.allowed_permission_roles?.length ? 'default' : 'outline'}>
                      {t.allowed_permission_roles?.length
                        ? `${t.allowed_permission_roles.length} role${t.allowed_permission_roles.length === 1 ? '' : 's'}`
                        : 'Anyone'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{t.fields.length}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {/* Archived templates are read-only (lineage preserved) — only the row remains. */}
                      {!t.archived && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Duplicate ${t.name}`}
                            disabled={duplicateMut.isPending}
                            onClick={() => duplicateMut.mutate(t)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Edit ${t.name}`}
                            onClick={() => openEdit(t)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            aria-label={`Remove ${t.name}`}
                            onClick={() => setRemoving(t)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit metadata */}
      <SlidePanel
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit template"
        width="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={editMut.isPending}>
              Cancel
            </Button>
            <Button onClick={() => editMut.mutate()} disabled={!editName.trim() || editMut.isPending}>
              {editMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="edit-template-name" className="text-sm font-medium">
              Template name
            </Label>
            <Input id="edit-template-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>
          <SendRoleSelector value={editRoles} onChange={setEditRoles} />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Pipeline types</Label>
            <p className="text-xs text-muted-foreground">
              Which pipeline types this template applies to. None selected = all pipeline types.
            </p>
            <div className="flex flex-wrap gap-2">
              {PIPELINE_TYPE_OPTIONS.map((pt) => {
                const on = editPipelineTypes.includes(pt);
                return (
                  <button
                    key={pt}
                    type="button"
                    onClick={() =>
                      setEditPipelineTypes((prev) => (on ? prev.filter((x) => x !== pt) : [...prev, pt]))
                    }
                    className={`px-3 py-1 rounded-full border text-sm capitalize ${
                      on ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {pt}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </SlidePanel>

      {/* Delete-or-archive confirm */}
      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &ldquo;{removing?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {usageQ.isLoading
                ? 'Checking whether this template has been used…'
                : willHardDelete
                  ? 'This template has never been used, so it will be permanently deleted.'
                  : 'This template has been used to create contracts, so it will be archived (hidden from pickers) — the contracts it produced are kept intact.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                removeMut.mutate();
              }}
              disabled={usageQ.isLoading || removeMut.isPending}
            >
              {removeMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {willHardDelete ? 'Delete' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
